import React, { useState, useContext } from 'react';
import '../agent_page.css'
import { AppContext } from '../../../provider.jsx'
import { getCaCsDocument, showCaCsDocument, updateCaCsData, getCaCsSlots, updateCaCsSlots, getCaCsSpecialSlots, updateCaCsSpecialSlots, getCaCsBankDetails } from '../../../controller/agent_data_controller.js';

// 12 fixed 2-hour slots
const SLOTS = Array.from({ length: 12 }, (_, i) => {
    const startH = i * 2;
    const endH = startH + 2;
    const fmt = (h) => `${h.toString().padStart(2, '0')}:00`;
    return { number: i + 1, label: `Slot ${i + 1}  (${fmt(startH)} – ${endH === 24 ? '00:00' : fmt(endH)})` };
});

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const CaCsDetails = ({ currentItem, onBack, onUpdate }) => {
    const { setPageIndex } = useContext(AppContext);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [documents, setDocumnets] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState({ certificate: null, idProof: null });
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    // Bank Details State
    const [bankDetails, setBankDetails] = useState(null);
    const [loadingBankDetails, setLoadingBankDetails] = useState(true);

    // Schedule state
    const [activeTab, setActiveTab] = useState('info'); // 'info', 'bank', or 'schedule'
    const [daySlots, setDaySlots] = useState(() =>
        Object.fromEntries(DAYS.map(d => [d, new Set()]))
    );
    const [specialSlotsByDate, setSpecialSlotsByDate] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);


    const fetchData = async () => {
        setLoading(true);
        setLoadingBankDetails(true);
        try {
            let docResult = await getCaCsDocument(currentItem.id);
            setDocumnets(docResult);

            const bankResult = await getCaCsBankDetails(currentItem.id);
            setBankDetails(bankResult);
            setLoadingBankDetails(false);

            // Fetch weekly slots: result = [ [day, slot_number], ... ]
            const slotsResult = await getCaCsSlots(currentItem.id);
            if (slotsResult.message === 'success') {
                const organized = Object.fromEntries(DAYS.map(d => [d, new Set()]));
                slotsResult.result.forEach(([day, slotNum]) => {
                    if (organized[day]) organized[day].add(slotNum);
                });
                setDaySlots(organized);
            }

            // Fetch special slots: result = [ [date, slot_number], ... ]
            const specialResult = await getCaCsSpecialSlots(currentItem.id);
            if (specialResult.message === 'success') {
                const byDate = {};
                specialResult.result.forEach(([date, slotNum]) => {
                    const key = typeof date === 'string' ? date : new Date(date).toISOString().split('T')[0];
                    if (!byDate[key]) byDate[key] = new Set();
                    byDate[key].add(slotNum);
                });
                setSpecialSlotsByDate(byDate);
            }
        } catch (err) {
            console.error("Error fetching admin view data:", err);
            setLoadingBankDetails(false);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, [currentItem.id]);

    const toggleWeeklySlot = (day, slotNum) => {
        setDaySlots(prev => {
            const updated = new Set(prev[day]);
            if (updated.has(slotNum)) updated.delete(slotNum);
            else updated.add(slotNum);
            return { ...prev, [day]: updated };
        });
    };

    const applyToAllWeekdays = (sourceDay) => {
        const sourceCopy = new Set(daySlots[sourceDay]);
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        setDaySlots(prev => {
            const updated = { ...prev };
            weekdays.forEach(d => { updated[d] = new Set(sourceCopy); });
            return updated;
        });
    };

    const handleSaveSchedule = async () => {
        setUpdating(true);
        try {
            const flattenedSlots = [];
            DAYS.forEach(day => {
                daySlots[day].forEach(slotNum => {
                    flattenedSlots.push({ day, slot_number: slotNum });
                });
            });
            const result = await updateCaCsSlots(currentItem.id, flattenedSlots);
            if (result === 'success') alert('Weekly schedule updated successfully!');
            else alert('Failed to update weekly schedule');
        } catch (error) {
            console.error('Update schedule error:', error);
        } finally {
            setUpdating(false);
        }
    };

    const toggleSpecialSlot = (slotNum) => {
        setSpecialSlotsByDate(prev => {
            const current = new Set(prev[selectedDate] || []);
            if (current.has(slotNum)) current.delete(slotNum);
            else current.add(slotNum);
            return { ...prev, [selectedDate]: current };
        });
    };

    const handleSaveSpecialSchedule = async () => {
        setUpdating(true);
        try {
            const slotNumbers = Array.from(specialSlotsByDate[selectedDate] || []);
            const result = await updateCaCsSpecialSlots(currentItem.id, selectedDate, slotNumbers);
            if (result === 'success') {
                alert(`Special schedule for ${selectedDate} updated successfully!`);
                fetchData();
            } else {
                alert('Failed to update special schedule');
            }
        } catch (error) {
            console.error('Update special schedule error:', error);
        } finally {
            setUpdating(false);
        }
    };

    const currentSpecialSet = specialSlotsByDate[selectedDate] || new Set();
    const definedSpecialDates = Object.keys(specialSlotsByDate).filter(d => specialSlotsByDate[d].size > 0).sort();


    // Initialize edited data when currentItem changes
    React.useEffect(() => {
        if (currentItem) {
            setEditedData({ ...currentItem });
            setIsEditing(false);
            setSelectedFiles({ certificate: null, idProof: null });
        }
    }, [currentItem]);

    const handleEdit = () => {
        setIsEditing(true);
    }

    const handleInputChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    }

    const handleFileChange = (field, file) => {
        setSelectedFiles(prev => ({
            ...prev,
            [field]: file
        }));
    }

    const handleUpdate = async () => {
        setUpdating(true);
        try {
            const result = await updateCaCsData(currentItem.id, editedData, selectedFiles);
            if (result === 'updated') {
                alert('CA/CS data updated successfully!');
                setIsEditing(false);
                if (onUpdate) {
                    onUpdate(editedData);
                }
                fetchData(); // Refresh documents if they were updated
            } else {
                alert('Failed to update CA/CS data: ' + result);
            }
        } catch (error) {
            console.error('Update error:', error);
            alert('An error occurred during update');
        } finally {
            setUpdating(false);
        }
    }

    const handleCancel = () => {
        setEditedData({ ...currentItem });
        setSelectedFiles({ certificate: null, idProof: null });
        setIsEditing(false);
    }

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            setPageIndex(0);
        }
    }

    if (!currentItem) {
        return <div>No CA/CS data available</div>;
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', margin: '30px 50px 17px 42px', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div onClick={() => { setPageIndex(0) }} className='submit-button-variable' style={{ marginRight: '30px' }}>
                        <span>🡐 back</span>
                    </div>
                    <p className='title-demo' style={{ margin: '0' }}>CA/CS Details</p>
                </div>
            </div>

            <div style={{ width: '100%', minHeight: '80%', display: 'flex', justifyContent: 'center' }}>
                <div className='content-div-demo' style={{ padding: '20px' }}>
                    <div style={{ borderBottom: '1px solid black', padding: '10px 20px', marginBottom: '20px', display: 'flex', gap: '30px' }}>
                        <p onClick={() => setActiveTab('info')} style={{ fontSize: '22px', fontWeight: '600', cursor: 'pointer', color: activeTab === 'info' ? 'blue' : 'black', borderBottom: activeTab === 'info' ? '2px solid blue' : 'none', paddingBottom: '5px' }}>CA/CS Information</p>
                        <p onClick={() => setActiveTab('bank')} style={{ fontSize: '22px', fontWeight: '600', cursor: 'pointer', color: activeTab === 'bank' ? 'blue' : 'black', borderBottom: activeTab === 'bank' ? '2px solid blue' : 'none', paddingBottom: '5px' }}>Bank Details</p>
                        <p onClick={() => setActiveTab('schedule')} style={{ fontSize: '22px', fontWeight: '600', cursor: 'pointer', color: activeTab === 'schedule' ? 'blue' : 'black', borderBottom: activeTab === 'schedule' ? '2px solid blue' : 'none', paddingBottom: '5px' }}>Manage Schedule</p>
                    </div>

                    {activeTab === 'info' ? (
                        <>
                            {/* Row 1: Name - Role */}
                            <div style={{ display: 'flex', gap: '40px', padding: '20px 0 10px 20px' }}>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Name</p>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editedData.name || ''}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.name}</p>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Role</p>
                                    {isEditing ? (
                                        <select
                                            value={editedData.role || ''}
                                            onChange={(e) => handleInputChange('role', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        >
                                            <option value="CA">CA</option>
                                            <option value="CS">CS</option>
                                        </select>
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.role}</p>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: Email - Phone */}
                            <div style={{ display: 'flex', gap: '40px', padding: '20px 0 10px 20px' }}>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Email</p>
                                    {isEditing ? (
                                        <input
                                            type="email"
                                            value={editedData.email || ''}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.email}</p>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Phone</p>
                                    {isEditing ? (
                                        <input
                                            type="tel"
                                            value={editedData.phone || ''}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.phone}</p>
                                    )}
                                </div>
                            </div>

                            {/* Row 3: Specialization - Registration Number */}
                            <div style={{ display: 'flex', gap: '40px', padding: '20px 0 10px 20px' }}>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Specialization</p>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editedData.specialization || ''}
                                            onChange={(e) => handleInputChange('specialization', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.specialization}</p>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Registration Number</p>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editedData.registrationNumber || ''}
                                            onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.registrationNumber}</p>
                                    )}
                                </div>
                            </div>

                            {/* Row 4: Timezone */}
                            <div style={{ display: 'flex', gap: '40px', padding: '10px 0 10px 20px' }}>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Timezone</p>
                                    {isEditing ? (
                                        <select
                                            value={editedData.timezone || 'Asia/Kolkata'}
                                            onChange={(e) => handleInputChange('timezone', e.target.value)}
                                            style={{ fontSize: '20px', fontWeight: '600', padding: '8px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
                                        >
                                            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                                            <option value="UTC">UTC</option>
                                            <option value="America/New_York">America/New_York (EST/EDT)</option>
                                            <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                                            <option value="Europe/London">Europe/London (GMT/BST)</option>
                                            <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                                            <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                                            <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                                            <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                                        </select>
                                    ) : (
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{currentItem.timezone || 'Asia/Kolkata'}</p>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}></div>
                            </div>


                            {/* Row 5: Certificate */}
                            <div style={{ padding: '20px 0 10px 20px' }}>
                                <p style={{ fontSize: '15px', paddingBottom: '10px' }}>Certificate</p>
                                {isEditing ? (
                                    <input
                                        style={{ height: '35px', marginBottom: '5px' }}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const maxFileSize = 5 * 1024 * 1024;
                                                if (file.size > maxFileSize) {
                                                    alert(`File "${file.name}" is too large. Max size is 5 MB.`);
                                                    e.target.value = "";
                                                    return;
                                                }
                                                handleFileChange('certificate', file);
                                            }
                                        }} type="file" id='certificate' />
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{documents.length > 0 ? documents[0][1] : 'none'}</p>
                                        {documents.length > 0 && (
                                            <div className='view-button' style={{ color: 'blue', cursor: 'pointer', fontSize: '16px' }}
                                                onClick={() => showCaCsDocument(documents[0][0])}
                                            >view</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Row 5: ID Proof */}
                            <div style={{ padding: '20px 0 10px 20px' }}>
                                <p style={{ fontSize: '15px', paddingBottom: '10px' }}>ID Proof</p>
                                {isEditing ? (
                                    <input
                                        style={{ height: '35px', marginBottom: '5px' }}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const maxFileSize = 5 * 1024 * 1024;
                                                if (file.size > maxFileSize) {
                                                    alert(`File "${file.name}" is too large. Max size is 5 MB.`);
                                                    e.target.value = "";
                                                    return;
                                                }
                                                handleFileChange('idProof', file);
                                            }
                                        }} type="file" id='idProof' />
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <p style={{ fontSize: '20px', fontWeight: '600' }}>{documents.length > 1 ? documents[1][1] : 'none'}</p>
                                        {documents.length > 1 && (
                                            <div className='view-button' style={{ color: 'blue', cursor: 'pointer', fontSize: '16px' }}
                                                onClick={() => showCaCsDocument(documents[1][0])}
                                            >view</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: '30px', display: 'flex', gap: '10px', padding: '20px' }}>
                                {!isEditing ? (
                                    <>
                                        <div className='submit-button' onClick={handleEdit} style={{ fontSize: '14px', padding: '8px 16px' }}>
                                            Edit Information
                                        </div>
                                        <div className='submit-button' onClick={handleBack} style={{ fontSize: '14px', padding: '8px 16px' }}>
                                            Back
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className='submit-button' onClick={updating ? null : handleUpdate} style={{ fontSize: '14px', padding: '8px 16px', backgroundColor: updating ? '#ccc' : '#28a745' }}>
                                            {updating ? 'Updating...' : 'Update'}
                                        </div>
                                        <div className='submit-button' onClick={handleCancel} style={{ fontSize: '14px', padding: '8px 16px', backgroundColor: '#dc3545' }}>
                                            Cancel
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    ) : activeTab === 'bank' ? (
                        <div style={{ padding: '20px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px', color: '#333' }}>
                                CA/CS Bank Details
                            </h3>
                            {loadingBankDetails ? (
                                <p style={{ color: '#666' }}>Loading bank details...</p>
                            ) : bankDetails ? (
                                <>
                                    <div style={{ marginBottom: '20px' }}>
                                        <p style={{ fontSize: '13px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bank Account Details</p>
                                        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                                            <p style={{ margin: '6px 0', fontSize: '15px' }}><strong>Bank Name:</strong> {bankDetails.bankName}</p>
                                            <p style={{ margin: '6px 0', fontSize: '15px' }}><strong>Account Name:</strong> {bankDetails.accountName}</p>
                                            <p style={{ margin: '6px 0', fontSize: '15px' }}><strong>Account Number:</strong> {bankDetails.accountNumber}</p>
                                            <p style={{ margin: '6px 0', fontSize: '15px' }}><strong>IFSC Code:</strong> {bankDetails.ifscCode}</p>
                                        </div>
                                    </div>

                                    {bankDetails.upiId && (
                                        <div style={{ marginBottom: '25px' }}>
                                            <p style={{ fontSize: '13px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>UPI Details</p>
                                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                                                <p style={{ margin: '6px 0', fontSize: '15px' }}><strong>UPI ID:</strong> {bankDetails.upiId}</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p style={{ color: '#d9534f', padding: '15px', backgroundColor: '#fdf3f2', borderRadius: '8px' }}>
                                    Bank details not available for this CA/CS.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div style={{ padding: '20px' }}>
                            <div style={{ borderBottom: '1px solid #ddd', paddingBottom: '10px', marginBottom: '20px', display: 'flex', gap: '30px' }}>
                                {['weekly', 'special'].map(tab => (
                                    <p key={tab} onClick={() => setActiveTab(tab === 'weekly' ? 'schedule' : 'special_schedule')} style={{
                                        fontSize: '18px', fontWeight: '600', cursor: 'pointer',
                                        color: (activeTab === 'schedule' && tab === 'weekly') || (activeTab === 'special_schedule' && tab === 'special') ? 'blue' : 'black',
                                        borderBottom: (activeTab === 'schedule' && tab === 'weekly') || (activeTab === 'special_schedule' && tab === 'special') ? '2px solid blue' : 'none',
                                        paddingBottom: '5px', textTransform: 'capitalize'
                                    }}>
                                        {tab === 'weekly' ? 'Weekly Schedule' : 'Special Date Schedule'}
                                    </p>
                                ))}
                            </div>

                            {activeTab === 'schedule' ? (
                                <div>
                                    {DAYS.map(day => (
                                        <div key={day} style={{ marginBottom: '25px', padding: '20px', border: '1px solid #e0e6ed', borderRadius: '12px', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748', margin: 0 }}>{day}</h3>
                                                <div style={{ display: 'flex', gap: '12px' }}>
                                                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(day) && (
                                                        <button
                                                            onClick={() => applyToAllWeekdays(day)}
                                                            style={{ padding: '6px 14px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#4a5568', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', transition: 'background 0.2s' }}>
                                                            Apply to all Weekdays
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setDaySlots(prev => ({ ...prev, [day]: new Set() }))}
                                                        style={{ padding: '6px 14px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#f56565', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600' }}>
                                                        Clear All
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                                                {SLOTS.map(slot => {
                                                    const isSelected = daySlots[day]?.has(slot.number);
                                                    return (
                                                        <div
                                                            key={slot.number}
                                                            onClick={() => toggleWeeklySlot(day, slot.number)}
                                                            style={{
                                                                padding: '12px',
                                                                borderRadius: '10px',
                                                                cursor: 'pointer',
                                                                textAlign: 'center',
                                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                backgroundColor: isSelected ? '#3182ce' : '#fff',
                                                                color: isSelected ? '#fff' : '#4a5568',
                                                                border: `2px solid ${isSelected ? '#3182ce' : '#e2e8f0'}`,
                                                                boxShadow: isSelected ? '0 4px 6px rgba(49, 130, 206, 0.2)' : 'none',
                                                                userSelect: 'none'
                                                            }}
                                                            onMouseOver={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#3182ce'; }}
                                                            onMouseOut={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                                        >
                                                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', opacity: isSelected ? 0.9 : 0.6 }}>Slot {slot.number}</p>
                                                            <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: '600' }}>{slot.label.split('(')[1].replace(')', '')}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {(!daySlots[day] || daySlots[day].size === 0) && (
                                                <div style={{ textAlign: 'center', padding: '15px', marginTop: '10px', backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px dashed #feb2b2' }}>
                                                    <p style={{ fontSize: '14px', color: '#c53030', margin: 0, fontWeight: '500' }}>Unavailable for this day</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <div className='submit-button' onClick={updating ? null : handleSaveSchedule}
                                        style={{
                                            marginTop: '10px', padding: '15px 30px', borderRadius: '10px', fontSize: '16px', fontWeight: '700',
                                            backgroundColor: updating ? '#a0aec0' : '#3182ce', boxShadow: '0 4px 14px rgba(49, 130, 206, 0.3)'
                                        }}>
                                        {updating ? 'Saving Changes...' : 'Save Weekly Schedule'}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div style={{ backgroundColor: '#edf2f7', padding: '25px', borderRadius: '15px', marginBottom: '30px' }}>
                                        <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                            <div style={{ minWidth: '220px' }}>
                                                <p style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>Select Special Date</p>
                                                <input
                                                    type="date"
                                                    value={selectedDate}
                                                    onChange={(e) => setSelectedDate(e.target.value)}
                                                    style={{ padding: '12px', width: '100%', border: '2px solid #cbd5e0', borderRadius: '8px', fontSize: '15px', fontWeight: '500' }}
                                                />
                                                <div className='submit-button' onClick={updating ? null : handleSaveSpecialSchedule}
                                                    style={{ marginTop: '15px', width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: updating ? '#a0aec0' : '#48bb78' }}>
                                                    {updating ? 'Saving...' : 'Save for Date'}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: '700', color: '#2d3748' }}>Availability for {selectedDate}</p>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                                                    {SLOTS.map(slot => {
                                                        const isSelected = currentSpecialSet.has(slot.number);
                                                        return (
                                                            <div
                                                                key={slot.number}
                                                                onClick={() => toggleSpecialSlot(slot.number)}
                                                                style={{
                                                                    padding: '10px',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    textAlign: 'center',
                                                                    transition: 'all 0.2s',
                                                                    backgroundColor: isSelected ? '#48bb78' : '#fff',
                                                                    color: isSelected ? '#fff' : '#4a5568',
                                                                    border: `2px solid ${isSelected ? '#48bb78' : '#e2e8f0'}`,
                                                                    userSelect: 'none'
                                                                }}
                                                            >
                                                                <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', opacity: 0.8 }}>SLOT {slot.number}</p>
                                                                <p style={{ margin: '2px 0 0', fontSize: '13px', fontWeight: '600' }}>{slot.label.split('(')[1].replace(')', '')}</p>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <p style={{ fontWeight: '600', marginBottom: '10px' }}>Defined Special Schedules:</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {definedSpecialDates.map(date => (
                                                <div key={date} onClick={() => setSelectedDate(date)} style={{
                                                    padding: '10px', backgroundColor: selectedDate === date ? '#e3f2fd' : 'white',
                                                    border: `1px solid ${selectedDate === date ? '#2196f3' : '#ddd'}`,
                                                    borderRadius: '4px', cursor: 'pointer'
                                                }}>
                                                    <p style={{ fontWeight: '600', fontSize: '14px' }}>{date}</p>
                                                    <p style={{ fontSize: '12px', color: '#666' }}>{specialSlotsByDate[date].size} slot{specialSlotsByDate[date].size !== 1 ? 's' : ''}</p>
                                                </div>
                                            ))}
                                            {definedSpecialDates.length === 0 && (
                                                <p style={{ fontSize: '14px', color: '#888' }}>No special schedules defined.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )

}

export default CaCsDetails;
